import { IApi } from '../../api'
import { createAction } from '../types'

export const prefix = 'clientIntake'

export const START_REQUEST = `${prefix}/START_REQUEST`
export const START_SUCCESS = `${prefix}/START_SUCCESS`
export const START_FAILURE = `${prefix}/START_FAILURE`

export const start = createAction((salesforceId: string) => ({
  type: [START_REQUEST, START_SUCCESS, START_FAILURE],
  promise: (api: IApi) => api.clientIntake.start(salesforceId),
}))

export const CURRENT_REQUEST = `${prefix}/CURRENT_REQUEST`
export const CURRENT_SUCCESS = `${prefix}/CURRENT_SUCCESS`
export const CURRENT_FAILURE = `${prefix}/CURRENT_FAILURE`

// Resolves the signed-in prospect's own data room, so the portal never needs an
// intake id in the URL.
export const loadCurrentIntake = createAction(() => ({
  type: [CURRENT_REQUEST, CURRENT_SUCCESS, CURRENT_FAILURE],
  promise: (api: IApi) => api.clientIntake.current(),
}))

export const REACTIVATE_REQUEST = `${prefix}/REACTIVATE_REQUEST`
export const REACTIVATE_SUCCESS = `${prefix}/REACTIVATE_SUCCESS`
export const REACTIVATE_FAILURE = `${prefix}/REACTIVATE_FAILURE`

// Self-serve reopen of an archived data room; the server derives which prospect
// from the caller's own membership.
export const reactivateIntake = createAction(() => ({
  type: [REACTIVATE_REQUEST, REACTIVATE_SUCCESS, REACTIVATE_FAILURE],
  promise: (api: IApi) => api.clientIntake.reactivate(),
}))

export const CLAIM_REQUEST = `${prefix}/CLAIM_REQUEST`
export const CLAIM_SUCCESS = `${prefix}/CLAIM_SUCCESS`
export const CLAIM_FAILURE = `${prefix}/CLAIM_FAILURE`

// One-time claim of an intake link sent before the portal had logins.
export const claimIntake = createAction((salesforceId: string) => ({
  type: [CLAIM_REQUEST, CLAIM_SUCCESS, CLAIM_FAILURE],
  promise: (api: IApi) => api.clientIntake.claim(salesforceId),
}))

export const CREATE_DRAFT_REQUEST = `${prefix}/CREATE_DRAFT_REQUEST`
export const CREATE_DRAFT_SUCCESS = `${prefix}/CREATE_DRAFT_SUCCESS`
export const CREATE_DRAFT_FAILURE = `${prefix}/CREATE_DRAFT_FAILURE`

// `draftFromOwnMailbox` is the retry after the server reports the caller has no
// delegate access to the company's BDO mailbox — see CreateIntakeDraft.
export const createIntakeDraft = createAction(
  (salesforceId: string, data?: { draftFromOwnMailbox?: boolean }) => ({
    type: [CREATE_DRAFT_REQUEST, CREATE_DRAFT_SUCCESS, CREATE_DRAFT_FAILURE],
    promise: (api: IApi) => api.clientIntake.createDraft(salesforceId, data),
  }),
)

export const LOAD_VENDORS_REQUEST = `${prefix}/LOAD_VENDORS_REQUEST`
export const LOAD_VENDORS_SUCCESS = `${prefix}/LOAD_VENDORS_SUCCESS`
export const LOAD_VENDORS_FAILURE = `${prefix}/LOAD_VENDORS_FAILURE`

export const loadVendors = createAction((id: string, data: object) => ({
  type: [LOAD_VENDORS_REQUEST, LOAD_VENDORS_SUCCESS, LOAD_VENDORS_FAILURE],
  promise: (api: IApi) => api.clientIntake.loadVendors(id, data),
}))

export const GET_CODAT_LINK_REQUEST = `${prefix}/GET_CODAT_LINK_REQUEST`
export const GET_CODAT_LINK_SUCCESS = `${prefix}/GET_CODAT_LINK_SUCCESS`
export const GET_CODAT_LINK_FAILURE = `${prefix}/GET_CODAT_LINK_FAILURE`

export const getCodatLink = createAction((salesforceId: string) => ({
  type: [GET_CODAT_LINK_REQUEST, GET_CODAT_LINK_SUCCESS, GET_CODAT_LINK_FAILURE],
  promise: (api: IApi) => api.clientIntake.getCodatLink(salesforceId),
}))

export const SAVE_CONTACT_REQUEST = `${prefix}/SAVE_CONTACT_REQUEST`
export const SAVE_CONTACT_SUCCESS = `${prefix}/SAVE_CONTACT_SUCCESS`
export const SAVE_CONTACT_FAILURE = `${prefix}/SAVE_CONTACT_FAILURE`

export const saveContact = createAction((salesforceId: string, data: object) => ({
  type: [SAVE_CONTACT_REQUEST, SAVE_CONTACT_SUCCESS, SAVE_CONTACT_FAILURE],
  promise: (api: IApi) => api.clientIntake.saveContact(salesforceId, data),
}))

export const SAVE_NOT_APPLICABLE_DOCUMENT_REQUEST = `${prefix}/SAVE_NOT_APPLICABLE_DOCUMENT_REQUEST`
export const SAVE_NOT_APPLICABLE_DOCUMENT_SUCCESS = `${prefix}/SAVE_NOT_APPLICABLE_DOCUMENT_SUCCESS`
export const SAVE_NOT_APPLICABLE_DOCUMENT_FAILURE = `${prefix}/SAVE_NOT_APPLICABLE_DOCUMENT_FAILURE`

export const saveNotApplicableDocument = createAction((salesforceId: string, data: object) => ({
  type: [
    SAVE_NOT_APPLICABLE_DOCUMENT_REQUEST,
    SAVE_NOT_APPLICABLE_DOCUMENT_SUCCESS,
    SAVE_NOT_APPLICABLE_DOCUMENT_FAILURE,
  ],
  promise: (api: IApi) => api.clientIntake.notApplicableDocument(salesforceId, data),
}))

export const SAVE_COMPANY_INFO_REQUEST = `${prefix}/SAVE_COMPANY_INFO_REQUEST`
export const SAVE_COMPANY_INFO_SUCCESS = `${prefix}/SAVE_COMPANY_INFO_SUCCESS`
export const SAVE_COMPANY_INFO_FAILURE = `${prefix}/SAVE_COMPANY_INFO_FAILURE`

export const saveCompanyInfo = createAction((salesforceId: string, data: object) => ({
  type: [SAVE_COMPANY_INFO_REQUEST, SAVE_COMPANY_INFO_SUCCESS, SAVE_COMPANY_INFO_FAILURE],
  promise: (api: IApi) => api.clientIntake.saveCompanyInfo(salesforceId, data),
}))

export const SAVE_PEOPLE_AND_TOOLS_REQUEST = `${prefix}/SAVE_PEOPLE_AND_TOOLS_REQUEST`
export const SAVE_PEOPLE_AND_TOOLS_SUCCESS = `${prefix}/SAVE_PEOPLE_AND_TOOLS_SUCCESS`
export const SAVE_PEOPLE_AND_TOOLS_FAILURE = `${prefix}/SAVE_PEOPLE_AND_TOOLS_FAILURE`

export const savePeopleAndTools = createAction((salesforceId: string, data: object) => ({
  type: [
    SAVE_PEOPLE_AND_TOOLS_REQUEST,
    SAVE_PEOPLE_AND_TOOLS_SUCCESS,
    SAVE_PEOPLE_AND_TOOLS_FAILURE,
  ],
  promise: (api: IApi) => api.clientIntake.savePeopleAndTools(salesforceId, data),
}))

export const UPLOAD_DOCUMENT_REQUEST = `${prefix}/UPLOAD_DOCUMENT_REQUEST`
export const UPLOAD_DOCUMENT_SUCCESS = `${prefix}/UPLOAD_DOCUMENT_SUCCESS`
export const UPLOAD_DOCUMENT_FAILURE = `${prefix}/UPLOAD_DOCUMENT_FAILURE`

export const uploadDocument = createAction((salesforceId: string, data: FormData) => ({
  type: [UPLOAD_DOCUMENT_REQUEST, UPLOAD_DOCUMENT_SUCCESS, UPLOAD_DOCUMENT_FAILURE],
  promise: (api: IApi) => api.clientIntake.uploadDocument(salesforceId, data),
}))

export const DELETE_DOCUMENT_REQUEST = `${prefix}/DELETE_DOCUMENT_REQUEST`
export const DELETE_DOCUMENT_SUCCESS = `${prefix}/DELETE_DOCUMENT_SUCCESS`
export const DELETE_DOCUMENT_FAILURE = `${prefix}/DELETE_DOCUMENT_FAILURE`

export const deleteDocument = createAction((salesforceId: string, fileId: string) => ({
  type: [DELETE_DOCUMENT_REQUEST, DELETE_DOCUMENT_SUCCESS, DELETE_DOCUMENT_FAILURE],
  promise: (api: IApi) => api.clientIntake.deleteDocument(salesforceId, fileId),
}))

export const SUBMIT_REQUEST = `${prefix}/SUBMIT_REQUEST`
export const SUBMIT_SUCCESS = `${prefix}/SUBMIT_SUCCESS`
export const SUBMIT_FAILURE = `${prefix}/SUBMIT_FAILURE`

export const submit = createAction((salesforceId: string) => ({
  type: [SUBMIT_REQUEST, SUBMIT_SUCCESS, SUBMIT_FAILURE],
  promise: (api: IApi) => api.clientIntake.submit(salesforceId),
}))
